/**
 * CA DER Charges Admin Component
 *
 * Displays and manages CA DER charge rates (PCIA, NBCs, Standby) from Firestore.
 * Rates are grouped by utility (PG&E, SCE, SDG&E) with inline editing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Button,
    Tooltip,
    Grid,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

// Utility color scheme (matching CADERChargesPanel)
const UTILITY_COLORS = {
    PGE: '#0072ce',
    SCE: '#f7941d',
    SDGE: '#00a651'
};

const UTILITY_NAMES = {
    PGE: 'Pacific Gas & Electric',
    SCE: 'Southern California Edison',
    SDGE: 'San Diego Gas & Electric'
};

const CHARGE_TYPE_LABELS = {
    pcia: 'Power Charge Indifference Adjustment (PCIA)',
    nuclear_decom: 'Nuclear Decommissioning',
    wildfire_fund: 'DWR Wildfire Fund',
    ppp: 'Public Purpose Programs',
    ctc: 'Competition Transition Charge',
    standby_reservation: 'Standby Reservation Charge'
};

const VOLTAGE_LABELS = {
    secondary: 'Secondary (<2.4kV)',
    primary: 'Primary (2.4-50kV)',
    transmission: 'Transmission (>50kV)'
};

function CADERChargesAdmin() {
    const { currentUser } = useAuth();
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});

    const fetchRates = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/ca-der/charge-rates`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rates: ${response.status}`);
            }

            const data = await response.json();
            setRates(data.rates || []);
        } catch (err) {
            console.error('Error fetching CA DER rates:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchRates();
    }, [fetchRates]);

    const handleEdit = (rate) => {
        setEditingId(rate.id);
        setEditValues({
            rate_value: rate.rate_value,
            source: rate.source || '',
            notes: rate.notes || '',
            billing_multiplier: rate.billing_multiplier || 1.0
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValues({});
    };

    const handleSave = async (rateId) => {
        if (!currentUser) return;
        setSaving(rateId);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/ca-der/charge-rates/${rateId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    rate_value: parseFloat(editValues.rate_value),
                    source: editValues.source,
                    notes: editValues.notes,
                    billing_multiplier: editValues.billing_multiplier !== undefined
                        ? parseFloat(editValues.billing_multiplier)
                        : undefined
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update rate');
            }

            // Refresh rates after save
            await fetchRates();
            setEditingId(null);
            setEditValues({});
        } catch (err) {
            console.error('Error saving rate:', err);
            setError(err.message);
        } finally {
            setSaving(null);
        }
    };

    // Group rates by utility, separating standby from NBC rates
    const ratesByUtility = rates.reduce((acc, rate) => {
        const utility = rate.utility || 'Unknown';
        if (!acc[utility]) acc[utility] = { nbc: [], standby: [] };

        if (rate.charge_type === 'standby_reservation') {
            acc[utility].standby.push(rate);
        } else {
            acc[utility].nbc.push(rate);
        }
        return acc;
    }, {});

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: vortxColors.gold }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ color: vortxColors.white }}>
                        CA DER Charge Rates
                    </Typography>
                    <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                        Manage PCIA, Non-Bypassable Charges, and Standby rates for California IOUs
                    </Typography>
                </Box>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchRates}
                    variant="outlined"
                    sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Info Card */}
            <Card sx={{ bgcolor: alpha(vortxColors.gold, 0.1), mb: 3 }}>
                <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <InfoIcon sx={{ color: vortxColors.gold, fontSize: 20, mt: 0.25 }} />
                        <Box>
                            <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                These rates are applied to DER projects in California IOU territories.
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Updates take effect immediately for new scenario calculations.
                                Rates are stored in the <code>ca_der_charge_rates</code> Firestore collection.
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Rates by Utility */}
            {rates.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: vortxColors.charcoal }}>
                    <Typography color="text.secondary">
                        No charge rates found. Run the seed script to populate initial rates.
                    </Typography>
                    <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', mt: 1 }}>
                        <code>python3 scripts/seed_ca_der_charge_rates.py</code>
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {['PGE', 'SCE', 'SDGE'].map((utility) => {
                        const utilityData = ratesByUtility[utility] || { nbc: [], standby: [] };
                        const { nbc: nbcRates, standby: standbyRates } = utilityData;
                        if (nbcRates.length === 0 && standbyRates.length === 0) return null;

                        // Sort standby rates by voltage level
                        const sortedStandby = [...standbyRates].sort((a, b) => {
                            const order = { secondary: 0, primary: 1, transmission: 2 };
                            return (order[a.voltage_level] || 0) - (order[b.voltage_level] || 0);
                        });

                        return (
                            <Grid item xs={12} key={utility}>
                                <Paper sx={{ bgcolor: vortxColors.charcoal, overflow: 'hidden' }}>
                                    {/* Utility Header */}
                                    <Box sx={{
                                        p: 2,
                                        bgcolor: alpha(UTILITY_COLORS[utility], 0.15),
                                        borderBottom: `2px solid ${UTILITY_COLORS[utility]}`
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: UTILITY_COLORS[utility]
                                            }} />
                                            <Typography variant="subtitle1" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                                                {UTILITY_NAMES[utility]}
                                            </Typography>
                                            <Chip
                                                label={utility}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(UTILITY_COLORS[utility], 0.3),
                                                    color: UTILITY_COLORS[utility],
                                                    fontWeight: 600
                                                }}
                                            />
                                            <Chip
                                                label={`${nbcRates.length + standbyRates.length} rates`}
                                                size="small"
                                                sx={{ bgcolor: alpha(vortxColors.stone, 0.2), color: vortxColors.stone }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Standby Charges Section */}
                                    {sortedStandby.length > 0 && (
                                        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(vortxColors.stone, 0.2)}` }}>
                                            <Typography variant="overline" sx={{ color: vortxColors.gold, fontWeight: 600, display: 'block', mb: 1 }}>
                                                Standby Reservation Charges
                                            </Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '20%' }}>Voltage Level</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '15%' }}>Rate ($/kW)</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '15%' }}>Multiplier</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '30%' }}>Source</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }}>Effective</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }} align="right">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {sortedStandby.map((rate) => (
                                                            <TableRow key={rate.id} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 500 }}>
                                                                        {VOLTAGE_LABELS[rate.voltage_level] || rate.voltage_level}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {editingId === rate.id ? (
                                                                        <TextField
                                                                            size="small"
                                                                            type="number"
                                                                            value={editValues.rate_value || ''}
                                                                            onChange={(e) => setEditValues(prev => ({ ...prev, rate_value: e.target.value }))}
                                                                            inputProps={{ step: 0.01 }}
                                                                            sx={{
                                                                                width: 100,
                                                                                '& .MuiInputBase-input': { color: vortxColors.white }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: UTILITY_COLORS[utility],
                                                                                fontWeight: 600,
                                                                                fontFamily: vortxTypography.mono
                                                                            }}
                                                                        >
                                                                            ${rate.rate_value?.toFixed(2)}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {editingId === rate.id ? (
                                                                        <TextField
                                                                            size="small"
                                                                            type="number"
                                                                            value={editValues.billing_multiplier || ''}
                                                                            onChange={(e) => setEditValues(prev => ({ ...prev, billing_multiplier: e.target.value }))}
                                                                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                                                                            InputProps={{
                                                                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                                            }}
                                                                            sx={{
                                                                                width: 100,
                                                                                '& .MuiInputBase-input': { color: vortxColors.white }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Chip
                                                                            label={`${(rate.billing_multiplier * 100).toFixed(0)}%`}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: rate.billing_multiplier < 1
                                                                                    ? alpha('#2ecc71', 0.2)
                                                                                    : alpha(vortxColors.stone, 0.2),
                                                                                color: rate.billing_multiplier < 1
                                                                                    ? '#2ecc71'
                                                                                    : vortxColors.stone,
                                                                                fontWeight: 600
                                                                            }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {editingId === rate.id ? (
                                                                        <TextField
                                                                            size="small"
                                                                            value={editValues.source}
                                                                            onChange={(e) => setEditValues(prev => ({ ...prev, source: e.target.value }))}
                                                                            fullWidth
                                                                            sx={{ '& .MuiInputBase-input': { color: vortxColors.white } }}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                                                            {rate.source}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ color: vortxColors.stone }}>
                                                                    {rate.effective_date || '-'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {editingId === rate.id ? (
                                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                                            <Tooltip title="Save">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleSave(rate.id)}
                                                                                    disabled={saving === rate.id}
                                                                                    sx={{ color: '#2ecc71' }}
                                                                                >
                                                                                    {saving === rate.id ? (
                                                                                        <CircularProgress size={16} />
                                                                                    ) : (
                                                                                        <SaveIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Cancel">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={handleCancelEdit}
                                                                                    sx={{ color: vortxColors.stone }}
                                                                                >
                                                                                    <CancelIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    ) : (
                                                                        <Tooltip title="Edit rate">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleEdit(rate)}
                                                                                sx={{ color: vortxColors.gold }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* NBC Rates Table */}
                                    {nbcRates.length > 0 && (
                                        <Box sx={{ p: 2 }}>
                                            <Typography variant="overline" sx={{ color: vortxColors.stone, display: 'block', mb: 1 }}>
                                                Non-Bypassable Charges
                                            </Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '25%' }}>Charge Type</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '15%' }}>Rate</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }}>Unit</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '30%' }}>Source</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }}>Effective</TableCell>
                                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }} align="right">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {nbcRates.map((rate) => (
                                                            <TableRow key={rate.id} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 500 }}>
                                                                        {CHARGE_TYPE_LABELS[rate.charge_type] || rate.charge_type}
                                                                    </Typography>
                                                                    {rate.vintage_year && (
                                                                        <Chip
                                                                            label={`Vintage ${rate.vintage_year}`}
                                                                            size="small"
                                                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: 18, bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {editingId === rate.id ? (
                                                                        <TextField
                                                                            size="small"
                                                                            type="number"
                                                                            value={editValues.rate_value || ''}
                                                                            onChange={(e) => setEditValues(prev => ({ ...prev, rate_value: e.target.value }))}
                                                                            inputProps={{ step: 0.00001 }}
                                                                            sx={{
                                                                                width: 120,
                                                                                '& .MuiInputBase-input': { color: vortxColors.white }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: UTILITY_COLORS[utility],
                                                                                fontWeight: 600,
                                                                                fontFamily: vortxTypography.mono
                                                                            }}
                                                                        >
                                                                            {rate.rate_unit === '$/kW-month'
                                                                                ? `$${rate.rate_value?.toFixed(2)}`
                                                                                : `$${rate.rate_value?.toFixed(5)}`
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ color: vortxColors.stone }}>
                                                                    {rate.rate_unit}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {editingId === rate.id ? (
                                                                        <TextField
                                                                            size="small"
                                                                            value={editValues.source}
                                                                            onChange={(e) => setEditValues(prev => ({ ...prev, source: e.target.value }))}
                                                                            fullWidth
                                                                            sx={{ '& .MuiInputBase-input': { color: vortxColors.white } }}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                                                            {rate.source}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ color: vortxColors.stone }}>
                                                                    {rate.effective_date || '-'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {editingId === rate.id ? (
                                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                                            <Tooltip title="Save">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleSave(rate.id)}
                                                                                    disabled={saving === rate.id}
                                                                                    sx={{ color: '#2ecc71' }}
                                                                                >
                                                                                    {saving === rate.id ? (
                                                                                        <CircularProgress size={16} />
                                                                                    ) : (
                                                                                        <SaveIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Cancel">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={handleCancelEdit}
                                                                                    sx={{ color: vortxColors.stone }}
                                                                                >
                                                                                    <CancelIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    ) : (
                                                                        <Tooltip title="Edit rate">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleEdit(rate)}
                                                                                sx={{ color: vortxColors.gold }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Summary Stats */}
            {rates.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Divider sx={{ borderColor: alpha(vortxColors.stone, 0.3), mb: 2 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: alpha(vortxColors.stone, 0.1), borderRadius: 1 }}>
                                <Typography variant="overline" sx={{ color: vortxColors.stone }}>
                                    Total Rates
                                </Typography>
                                <Typography variant="h5" sx={{ color: vortxColors.white }}>
                                    {rates.length}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: alpha(vortxColors.stone, 0.1), borderRadius: 1 }}>
                                <Typography variant="overline" sx={{ color: vortxColors.stone }}>
                                    Utilities Covered
                                </Typography>
                                <Typography variant="h5" sx={{ color: vortxColors.white }}>
                                    {Object.keys(ratesByUtility).length}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: alpha(vortxColors.stone, 0.1), borderRadius: 1 }}>
                                <Typography variant="overline" sx={{ color: vortxColors.stone }}>
                                    Last Updated
                                </Typography>
                                <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                    {rates[0]?.updated_at
                                        ? new Date(rates[0].updated_at).toLocaleDateString()
                                        : '-'
                                    }
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

export default CADERChargesAdmin;
